/**
 * Inference tasks API slice.
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const inferenceTasksApi = {
  fetchTaskModels: async () => {
    const data = await fetchWithAuth('/json/inference-tasks/models');
    if (data.error) throw new ApiError(data.message || 'Failed to load task models', data.status || 400, data);
    return data.models || [];
  },

  fetchTaskBots: async () => {
    const data = await fetchWithAuth('/json/inference-tasks/bots');
    if (data.error) throw new ApiError(data.message || 'Failed to load bots', data.status || 400, data);
    return data.bots || [];
  },

  fetchTasks: async (page = 1, { workSessionId } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    if (workSessionId) params.set('workSessionId', String(workSessionId));
    const data = await fetchWithAuth(`/json/inference-tasks?${params.toString()}`);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch inference tasks', 400, data);
    }

    return {
      tasks: data.tasks,
      pagination: data.pagination,
    };
  },

  fetchTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/inference-tasks/${encodeURIComponent(taskId)}`);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch inference task', data.status || 400, data);
    }

    return data.task;
  },

  createTask: async ({ name, instructions, modelIds, parameters, conversationId, schedule, emailDelivery, timezone, collectionId, workSessionId, agentId, orchestratorModel }) => {
    const body = {
      name,
      instructions,
      modelIds,
      parameters,
      conversationId,
      schedule,
      emailDelivery,
      timezone,
      ...(collectionId && { collectionId }),
      ...(workSessionId && { workSessionId }),
      ...(agentId && { agentId }),
      ...(orchestratorModel !== undefined && { orchestratorModel }),
    };

    const data = await fetchWithAuth('/json/inference-tasks/new', {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to create inference task', 400, data);
    }

    return data.task;
  },

  updateTask: async (taskId, updates) => {
    const data = await fetchWithAuth(`/json/inference-tasks/${taskId}`, {
      method: 'PUT',
      body: updates,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update inference task', 400, data);
    }

    return data.task;
  },

  deleteTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/inference-tasks/${taskId}`, {
      method: 'DELETE',
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete inference task', 400, data);
    }

    return data;
  },

  runTaskNow: async (taskId) => {
    const data = await fetchWithAuth(`/json/inference-tasks/${taskId}/run-now`, {
      method: 'POST',
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to run task', 400, data);
    }

    return data;
  },

  favoriteTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-tasks/${encodeURIComponent(taskId)}`, {
      method: 'POST',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to favorite task', data.status || 400, data);
    return !data.error;
  },

  unfavoriteTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-tasks/${encodeURIComponent(taskId)}`, {
      method: 'DELETE',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to unfavorite task', data.status || 400, data);
    return !data.error;
  },
};
